// src/components/core/entity/EntityChannelsCard/ContactPersonModal/Step2.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect } from 'react';
import { Form, Card, Flex, Alert } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleEmail from 'components/SimpleComponents/SimpleEmail';
import SimplePhone from 'components/SimpleComponents/SimplePhone';
import SimpleDatePicker from 'components/SimpleComponents/SimpleDatePicker';
import SimpleTextArea from 'components/SimpleComponents/SimpleTextArea';
import SimpleSwitch from 'components/SimpleComponents/SimpleSwitch';
import { parsePhoneToDisplay } from 'utils/helpers';

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const ContactPersonDataContent = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { form, goFocus } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const entityFound = Form.useWatch('_entityFound', form);
  const searchResult = Form.useWatch('_searchResult', form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Auto-focus appropriate field
  useEffect(() => {
    if (!entityFound) {
      const timer = setTimeout(() => {
        goFocus?.('personName', true);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [entityFound, goFocus]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────

  // MODE A: Entity found - show confirmation card
  if (entityFound && searchResult) {
    const entityEmail = searchResult.contacts?.find(c => c.contactType === 'email')?.value;
    const entityPhone = searchResult.contacts?.find(c => c.contactType === 'phone');
    const phoneDisplay = entityPhone
      ? parsePhoneToDisplay(entityPhone.value, entityPhone.countryCode)
      : null;

    return (
      <>
        <Alert
          type="success"
          title={translateX('person_found_confirm')}
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Card styles={{ body: { padding: 16 } }} style={{ marginBottom: 16 }}>
          <Flex vertical gap={8}>
            <Flex align="center" gap={8}>
              <UserOutlined style={{ color: '#9c27b0' }} />
              <span className="font-weight-bold">{searchResult.name}</span>
            </Flex>
            {entityEmail && (
              <Flex align="center" gap={8}>
                <MailOutlined style={{ color: '#999' }} />
                <span>{entityEmail}</span>
              </Flex>
            )}
            {phoneDisplay && (
              <Flex align="center" gap={8}>
                <PhoneOutlined style={{ color: '#999' }} />
                <span>{phoneDisplay}</span>
              </Flex>
            )}
          </Flex>
        </Card>

        <SimpleTextArea
          name="note"
          label="note"
          rows={2}
          maxLength={200}
          placeholder="note_placeholder"
        />

        {/*<SimpleSwitch name="isPrimary" label="favorite" showLabel />*/}
      </>
    );
  }

  // MODE B: Entity not found - show creation form
  return (
    <>
      <Alert
        type="info"
        title={translateX('person_not_found_create')}
        showIcon
        style={{ marginBottom: 16 }}
      />

      <SimpleInput name="personName" label="name" required maxLength={100} />

      <SimpleEmail
        name={['personEmail', 'value']}
        label="email"
        placeholder="contact@example.com"
      />

      <SimplePhone name={['personPhone', 'value']} label="phone" international />

      <SimpleDatePicker name="personBirthdate" label="birthdate" />

      <SimpleTextArea
        name="note"
        label="note"
        rows={2}
        maxLength={200}
        placeholder="note_placeholder"
      />

      {/*<SimpleSwitch name="isPrimary" label="favorite" showLabel />*/}
    </>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const contactPersonDataStep = () => ({
  stepTitle: 'contact_data',
  stepTooltip: 'contact_person',
  content: () => <ContactPersonDataContent />,
  // Validation is handled in handleFinish for dynamic requirements
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default contactPersonDataStep;
